import { FC, useMemo } from "react";
import {
  styled,
  SelectBox,
  Grid,
  FieldLabel,
} from "@monolith-forensics/monolith-ui";
import { ImportFieldDefinition, ColumnMapping } from "../DataImport.types";

interface ColumnMappingStepProps {
  fields: ImportFieldDefinition[];
  csvColumnNames: string[];
  csvRows: Record<string, string>[];
  columnMapping: ColumnMapping;
  onMappingChange: (mapping: ColumnMapping) => void;
}

const ColumnMappingStep: FC<ColumnMappingStepProps> = ({
  fields,
  csvColumnNames,
  csvRows,
  columnMapping,
  onMappingChange,
}) => {
  const columnOptions = useMemo(
    () =>
      csvColumnNames.map((col) => ({
        value: col,
        label: col,
      })),
    [csvColumnNames]
  );

  const handleFieldMappingChange = (
    fieldKey: string,
    csvColumn: string | null
  ) => {
    onMappingChange({
      ...columnMapping,
      [fieldKey]: csvColumn || null,
    });
  };

  const getPreviewValues = (csvColumn: string | null): string[] => {
    if (!csvColumn) return [];
    return csvRows
      .slice(0, 3)
      .map((row) => row[csvColumn] || "")
      .filter((v) => v !== "");
  };

  return (
    <Container>
      <Header>
        <HeaderTitle>Map CSV Columns to Fields</HeaderTitle>
        <HeaderSubtext>
          Select which CSV column corresponds to each field. Required fields are
          marked with an asterisk.
        </HeaderSubtext>
      </Header>

      <MappingList>
        <Grid col={1} style={{ rowGap: 20 }}>
          {fields.map((field) => {
            const mappedColumn = columnMapping[field.key] || null;
            const preview = getPreviewValues(mappedColumn);

            return (
              <MappingItem key={field.key}>
                <FieldLabel
                  description={field.description}
                  asterisk={field.required}
                  style={{ width: 250 }}
                >
                  {field.label}
                </FieldLabel>
                <SelectBoxWrapper>
                  <SelectBox
                    size="sm"
                    variant="outlined"
                    placeholder="Select CSV column..."
                    required={field.required}
                    searchable
                    clearable
                    data={columnOptions}
                    value={mappedColumn || undefined}
                    onChange={(value: string | null) =>
                      handleFieldMappingChange(field.key, value)
                    }
                  />
                </SelectBoxWrapper>
                <PreviewValues>
                  {preview.length > 0 ? (
                    preview.map((val, i) => (
                      <PreviewChip key={i}>{val}</PreviewChip>
                    ))
                  ) : (
                    <PreviewPlaceholder>No preview</PreviewPlaceholder>
                  )}
                </PreviewValues>
              </MappingItem>
            );
          })}
        </Grid>
      </MappingList>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const HeaderTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
`;

const HeaderSubtext = styled.div`
  font-size: 13px;
  color: ${(props) => props.theme.palette.text.secondary};
`;

const MappingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MappingItem = styled.div`
  display: grid;
  grid-template-columns: 1fr 3fr 1fr;
  align-items: center;
  gap: 12px;
`;

const SelectBoxWrapper = styled.div`
  flex: 1;
  width: 500px;
  margin: 0 auto;
`;

const PreviewValues = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
  flex-shrink: 0;
  width: 240px;
`;

const PreviewChip = styled.span`
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 4px;
  background: ${(props) => props.theme.palette.background.default};
  border: 1px solid ${(props) => props.theme.palette.divider};
  color: ${(props) => props.theme.palette.text.secondary};
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PreviewPlaceholder = styled.span`
  font-size: 11px;
  color: ${(props) => props.theme.palette.text.secondary};
  opacity: 0.5;
`;

export default ColumnMappingStep;
